/**
 * Unified Job Schema for Blue-Collar Trades
 * All jobs are normalized to this structure for consistent matching
 */

export const SKILL_CATEGORIES = {
  'Carpentry': {
    label: 'Carpentry',
    keywords: ['carpentry', 'carpenter', 'framing', 'finishing', 'wood', 'furniture'],
    description: 'Framing, finishing, furniture building, and woodwork'
  },
  'Electrical': {
    label: 'Electrical',
    keywords: ['electrical', 'electrician', 'wiring', 'circuit', 'installation', 'power'],
    description: 'Wiring, circuit installation, electrical systems, lighting'
  },
  'Plumbing': {
    label: 'Plumbing',
    keywords: ['plumbing', 'plumber', 'pipe', 'water', 'drainage', 'sewage'],
    description: 'Pipe installation, water systems, drainage, repairs'
  },
  'Welding': {
    label: 'Welding',
    keywords: ['welding', 'welder', 'weld', 'fabrication', 'smaw', 'metal', 'mig', 'tig'],
    description: 'SMAW, MIG, TIG welding, structural steel, metal fabrication'
  },
  'General Labor': {
    label: 'General Labor',
    keywords: ['labor', 'laborer', 'general', 'construction', 'manual', 'helper'],
    description: 'General construction, site preparation, material handling'
  },
  'Mason': {
    label: 'Mason',
    keywords: ['mason', 'masonry', 'brick', 'stone', 'concrete', 'mortar'],
    description: 'Bricklaying, stonework, concrete work, masonry'
  },
  'Mechanical': {
    label: 'Mechanical',
    keywords: ['mechanical', 'hvac', 'machinery', 'repair', 'hydraulic'],
    description: 'HVAC systems, machinery repair, industrial equipment'
  }
};

export const EXPERIENCE_LEVELS = {
  'entry': { label: 'Entry Level', minYears: 0, maxYears: 1 },
  'intermediate': { label: 'Intermediate', minYears: 2, maxYears: 5 },
  'advanced': { label: 'Advanced', minYears: 5, maxYears: 10 },
  'expert': { label: 'Expert', minYears: 10, maxYears: 100 }
};

export const JOB_TYPES = ['Fulltime', 'Parttime', 'Freelance', 'Contract', 'Temporary'];

export const VERIFICATION_LEVELS = {
  'none': { label: 'Laborer', multiplier: 1 },
  'skilled': { label: 'Skilled', multiplier: 1.5 },
  'specialist': { label: 'Specialist', multiplier: 2 }
};

/**
 * Luzon locations with geolocation data
 */
export const LUZON_LOCATIONS = {
  'Metro Manila': { lat: 14.5995, lng: 120.9842, minWage: 645, region: 'NCR' },
  'Makati': { lat: 14.5549, lng: 121.0193, minWage: 645, region: 'NCR' },
  'Taguig': { lat: 14.5794, lng: 121.0554, minWage: 645, region: 'NCR' },
  'Quezon City': { lat: 14.6091, lng: 121.0223, minWage: 645, region: 'NCR' },
  'Caloocan': { lat: 14.6226, lng: 120.9605, minWage: 645, region: 'NCR' },
  'Pasay': { lat: 14.5500, lng: 121.0000, minWage: 645, region: 'NCR' },
  'Pasig': { lat: 14.5736, lng: 121.0911, minWage: 645, region: 'NCR' },
  'Marikina': { lat: 14.6418, lng: 121.1619, minWage: 645, region: 'NCR' },
  'Parañaque': { lat: 14.3542, lng: 121.0122, minWage: 645, region: 'NCR' },
  'Las Piñas': { lat: 14.3567, lng: 120.9789, minWage: 645, region: 'NCR' },
  'Quirino': { lat: 14.7522, lng: 121.0093, minWage: 645, region: 'NCR' },
  'San Juan': { lat: 14.5898, lng: 121.0272, minWage: 645, region: 'NCR' },
  'Malabon': { lat: 14.6511, lng: 120.9561, minWage: 645, region: 'NCR' },
  'Navotas': { lat: 14.6586, lng: 120.9294, minWage: 645, region: 'NCR' },
  'Valenzuela': { lat: 14.7139, lng: 120.9814, minWage: 645, region: 'NCR' },
  'Muntinlupa': { lat: 14.3867, lng: 121.0519, minWage: 645, region: 'NCR' },
  'Antipolo': { lat: 14.5880, lng: 121.1775, minWage: 600, region: 'Calabarzon' },
  'Cavite City': { lat: 14.4694, lng: 120.8867, minWage: 588, region: 'Calabarzon' },
  'Dasmariñas': { lat: 14.2917, lng: 120.9231, minWage: 588, region: 'Calabarzon' },
  'Kawit': { lat: 14.4425, lng: 120.8392, minWage: 588, region: 'Calabarzon' },
  'Laguna': { lat: 14.3169, lng: 121.2323, minWage: 600, region: 'Calabarzon' },
  'San Pedro': { lat: 14.3506, lng: 121.0139, minWage: 600, region: 'Calabarzon' },
  'Tanay': { lat: 14.5756, lng: 121.3653, minWage: 600, region: 'Calabarzon' },
  'Cainta': { lat: 14.5508, lng: 121.2867, minWage: 600, region: 'Calabarzon' },
  'Baguio': { lat: 16.4023, lng: 120.5960, minWage: 527, region: 'CAR' },
  'Dagupan': { lat: 16.0411, lng: 120.3353, minWage: 554, region: 'Region I' },
  'Laoag': { lat: 18.1946, lng: 120.5984, minWage: 515, region: 'Region I' },
};

/**
 * Unified Job Schema Template
 * All jobs should follow this structure
 */
export const createUnifiedJob = (jobData) => {
  return {
    // Identifiers
    id: jobData.id || null,
    sourceType: jobData.sourceType || 'manual', // 'manual' for admin, 'request' for job requests
    sourceRequestId: jobData.sourceRequestId || null,

    // Basic Info
    title: jobData.title || '',
    description: jobData.description || '',
    companyName: jobData.companyName || '',
    
    // Skill Information - IMPORTANT FOR MATCHING
    primary_skill: jobData.primary_skill || jobData.skillField || '',
    skills: normalizeSkillsArray(jobData.skills),
    skillLevel: jobData.skillLevel || 'laborer',
    experience: jobData.experience || 'entry',
    
    // Location Info
    location: jobData.location || '',
    coordinates: jobData.coordinates || null,
    latitude: jobData.latitude || null,
    longitude: jobData.longitude || null,
    
    // Job Details
    type: jobData.type || 'Fulltime',
    workplace: jobData.workplace || 'On-site',
    hours: jobData.hours || '',
    vacancy: jobData.vacancy || '1',
    rate: jobData.rate || 'Negotiable',
    
    // Employment Details
    education: jobData.education || '',
    benefits: jobData.benefits || [],
    
    // Meta
    status: jobData.status || 'approved',
    icon: jobData.icon || '💼',
    image: jobData.image || '/images/sourced/job-default.png',
    
    // Timestamps
    createdAt: jobData.createdAt || new Date().toISOString(),
    updatedAt: jobData.updatedAt || new Date().toISOString(),
    postedBy: jobData.postedBy || null
  };
};

/**
 * Normalize skills to array of strings
 */
export const normalizeSkillsArray = (skills) => {
  if (!skills) return [];
  
  if (Array.isArray(skills)) {
    return skills
      .map(s => (typeof s === 'string' ? s.trim() : ''))
      .filter(s => s.length > 0);
  }
  
  if (typeof skills === 'string') {
    return skills
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }
  
  return [];
};

/**
 * Identify primary skill category from job
 */
export const identifyPrimarySkill = (jobTitle, jobDescription, jobSkills) => {
  const text = `${jobTitle} ${jobDescription} ${jobSkills.join(' ')}`.toLowerCase();
  
  for (const [skillKey, skillData] of Object.entries(SKILL_CATEGORIES)) {
    const keywords = skillData.keywords;
    if (keywords.some(kw => text.includes(kw))) {
      return skillKey;
    }
  }
  
  return 'General Labor'; // Default fallback
};

/**
 * Validate and repair job structure
 */
export const validateJob = (job) => {
  const validated = createUnifiedJob(job);
  
  // If no primary skill, identify it
  if (!validated.primary_skill) {
    validated.primary_skill = identifyPrimarySkill(
      validated.title,
      validated.description,
      validated.skills
    );
  }
  
  // Ensure coordinates match lat/lng
  if (validated.coordinates && !validated.latitude) {
    validated.latitude = validated.coordinates.latitude;
    validated.longitude = validated.coordinates.longitude;
  }
  
  return validated;
};

/**
 * Extract semantic text for ML matching
 * Combines all textual info for better embedding
 */
export const getJobSemanticText = (job) => {
  const unified = typeof job.primary_skill === 'string' ? job : validateJob(job);
  
  const parts = [
    unified.title,
    unified.description,
    SKILL_CATEGORIES[unified.primary_skill]?.description || '',
    unified.skills.join(' '),
    unified.skillLevel,
    unified.experience,
    unified.education
  ];
  
  return parts
    .filter(p => p && typeof p === 'string' && p.length > 0)
    .join(' ')
    .toLowerCase();
};

/**
 * Create unified User Profile matching Job Schema
 * This ensures ML can compare user preferences with jobs on the same terms
 */
export const createUserProfile = (preferences) => {
  return {
    // Identifiers
    id: preferences.id || null,
    userType: 'seeker',
    
    // Basic Info (maps to job requirements)
    title: `Seeking ${preferences.skill} work`,
    description: preferences.description || `Looking for ${preferences.skill} opportunities at ${preferences.skillLevel || 'any'} level`,
    companyName: '', // Not applicable for users
    
    // Skill Information - IMPORTANT FOR MATCHING
    primary_skill: preferences.skill || '',
    skills: [preferences.skill].filter(s => s),  // Primary skill as array
    skillLevel: preferences.skillLevel || 'laborer',  // Maps to verificationLevel
    experience: preferences.experience || 'entry',
    
    // Location Info - User can work in multiple areas
    location: preferences.location?.name || '',
    coordinates: preferences.location || null,
    latitude: preferences.location?.latitude || null,
    longitude: preferences.location?.longitude || null,
    
    // Job Preferences (maps to job.type, workplace, hours)
    type: preferences.jobTypes?.length > 0 ? preferences.jobTypes[0] : 'Any',  // Preferred job type
    workplace: preferences.workplace || 'On-site',  // Preferred work location
    hours: preferences.hours || 'Full-time',  // Preferred hours
    
    // Employment Details
    education: preferences.education || '',
    benefits: preferences.desiredBenefits || [],
    
    // Matching Parameters
    radius: preferences.radius || 50,  // Willing to work within X km
    flexibleTypes: preferences.jobTypes || [],  // Multiple acceptable job types
    availability: preferences.availability || 'flexible',
    
    // Metadata
    status: 'active',
    
    // Timestamps
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

/**
 * Extract user preference semantic text
 * Now based on structured profile, not loose preferences
 */
export const getUserPreferenceSemanticText = (preferences) => {
  // Create full profile for semantic extraction
  const profile = typeof preferences.primary_skill === 'string' 
    ? preferences  // Already a profile
    : createUserProfile(preferences);  // Convert preferences to profile
  
  const parts = [
    profile.title,
    profile.description,
    profile.primary_skill ? SKILL_CATEGORIES[profile.primary_skill]?.description : '',
    profile.skills.join(' '),
    profile.skillLevel,
    profile.experience,
    profile.education,
    profile.type ? `Interested in ${profile.type} work` : '',
    profile.workplace ? `Prefers ${profile.workplace} work` : '',
    profile.flexibleTypes.length > 0 ? `Open to ${profile.flexibleTypes.join(' or ')}` : ''
  ];
  
  return parts
    .filter(p => p && typeof p === 'string' && p.length > 0)
    .join(' ')
    .toLowerCase();
};

/**
 * Create matching score criteria object that mirrors job structure
 * This makes ML comparisons structural and semantically meaningful
 */
export const createMatchingCriteria = (preferences) => {
  const profile = typeof preferences.primary_skill === 'string'
    ? preferences
    : createUserProfile(preferences);
  
  return {
    userProfile: profile,
    userSemanticText: getUserPreferenceSemanticText(profile),
    
    // Structured matching criteria
    requiredSkill: profile.primary_skill,
    requiredExperience: profile.experience,
    requiredSkillLevel: profile.skillLevel,
    
    location: {
      latitude: profile.latitude,
      longitude: profile.longitude,
      radius: profile.radius
    },
    
    preferredJobTypes: profile.flexibleTypes.length > 0 ? profile.flexibleTypes : ['Fulltime', 'Parttime', 'Freelance', 'Contract', 'Temporary'],
    preferredWorkplace: profile.workplace,
    
    // Scoring weights
    weights: {
      skillMatch: 0.35,      // Exact skill match
      experienceMatch: 0.20, // Experience level match
      skillLevelMatch: 0.15, // Skill level compatibility
      locationMatch: 0.20,   // Geographic proximity
      typeMatch: 0.10        // Job type preference
    }
  };
};

const jobSchema = {
  SKILL_CATEGORIES,
  EXPERIENCE_LEVELS,
  JOB_TYPES,
  VERIFICATION_LEVELS,
  LUZON_LOCATIONS,
  createUnifiedJob,
  normalizeSkillsArray,
  identifyPrimarySkill,
  validateJob,
  getJobSemanticText,
  getUserPreferenceSemanticText,
  createUserProfile,
  createMatchingCriteria
};

export default jobSchema;
